import React, {useState} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {makeStyles} from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import {Add, DeleteOutlined, EditOutlined} from '@material-ui/icons';
import DataGrid from '../../../shared/datagrid/DataGrid.tsx';
import InstructionMessage from '../../../shared/instructionmessage/InstructionMessage.js';
import useConfirmationDialog from '../../../shared/confirmationdialog/useConfirmationDialog.js';
import {criarColunaOpcoes} from '../../components/ColunaOpcoes.jsx';
import DialogCalendarioInscricao, {FORMATO_DATA_HORA_WIRE} from '../../dialogs/inscricao/DialogCalendarioInscricao.jsx';
import {ouTraco, SEM_VALOR} from '../../components/inscricao/documentoInscricao.js';
import {PAGINACAO_PADRAO} from '../../components/paginacao.js';
import {buttonTableCell, containedButton, tituloSecao} from '../../../../utils/styles.js';

const useStyles = makeStyles(theme => ({
    sectionTitle: {
        ...tituloSecao(theme),
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2)
    },
    ...buttonTableCell(theme),
    ...containedButton(theme, false, false)
}));

const formatarDataHora = valor => {
    if (!valor) return SEM_VALOR;
    const data = moment(valor, FORMATO_DATA_HORA_WIRE);
    return data.isValid() ? data.format('DD/MM/YYYY HH:mm') : String(valor);
};

/*
 * Etapa "Calendário" (só do administrativo): os calendários individuais da
 * inscrição (entrega de documentos e de documentos pendentes). Uma seção só,
 * em largura total, como a etapa Documentos.
 */
export default function EtapaCalendario({watch, setValue, tiposCalendario = []}) {
    const classes = useStyles();
    const confirmation = useConfirmationDialog();
    // Índice do calendário em edição: -1 para adicionar, null com o diálogo fechado.
    const [indiceCalendario, setIndiceCalendario] = useState(null);

    const calendarios = watch('calendarios') || [];
    const calendarioEmEdicao = indiceCalendario !== null && indiceCalendario > -1 ? calendarios[indiceCalendario] : null;

    const salvarCalendario = calendario => {
        const atual = [...calendarios];
        if (calendarioEmEdicao) {
            atual[indiceCalendario] = calendario;
        } else {
            atual.push(calendario);
        }
        setValue('calendarios', atual, {shouldDirty: true});
    };

    const removerCalendario = posicao => {
        confirmation('Tem certeza que deseja remover o calendário?', () =>
            setValue('calendarios', calendarios.filter((item, atual) => atual !== posicao), {shouldDirty: true}));
    };

    return (
        <>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <h4 className={classes.sectionTitle}>Calendário</h4>
                    <InstructionMessage
                        title='Orientações sobre o Calendário'
                        type='info'
                        variant='standard'
                        collapsible={true}
                        defaultExpanded={true}
                        items={['Agende os compromissos da inscrição com os tipos de calendário do processo de seleção, com início e término.']}/>
                </Grid>
                <Grid item xs={12}>
                    <DataGrid
                        toolbarContent={(
                            <Button variant='contained' className={classes.containedButton} startIcon={<Add/>}
                                    onClick={() => setIndiceCalendario(-1)}>
                                Adicionar
                            </Button>
                        )}
                        tableContainerProps={{component: 'div'}}
                        rows={calendarios}
                        paginate
                        paginateOptions={PAGINACAO_PADRAO}
                        noRecordsText='Nenhum calendário cadastrado.'
                        columns={[
                            {label: 'Tipo', field: 'descricaoTipo', valueGetter: valor => ouTraco(valor)},
                            {label: 'Início', field: 'dataInicio', valueGetter: valor => formatarDataHora(valor)},
                            {label: 'Término', field: 'dataFim', valueGetter: valor => formatarDataHora(valor)},
                            criarColunaOpcoes(row => {
                                const posicao = calendarios.indexOf(row);
                                return [
                                    {titulo: 'Editar', icone: <EditOutlined/>, onClick: () => setIndiceCalendario(posicao)},
                                    {titulo: 'Remover', icone: <DeleteOutlined/>, onClick: () => removerCalendario(posicao)}
                                ];
                            }, {className: classes.buttonTableCell})
                        ]}/>
                </Grid>
            </Grid>
            <DialogCalendarioInscricao
                open={indiceCalendario !== null}
                onClose={() => setIndiceCalendario(null)}
                title={calendarioEmEdicao ? 'Editar Calendário' : 'Adicionar Calendário'}
                value={calendarioEmEdicao}
                tiposCalendario={tiposCalendario}
                onSubmit={salvarCalendario}/>
        </>
    );
}

EtapaCalendario.propTypes = {
    watch: PropTypes.func.isRequired,
    setValue: PropTypes.func.isRequired,
    tiposCalendario: PropTypes.array
};
